import React, { useEffect, useRef, useState } from "react";
import "../styles/team-members.css";
import { useNavigate } from "react-router-dom";
import { canEditProfile } from "../utils/auth";
import { toAbsoluteUrl } from "../utils/profilePictureUtils";
import EditIcon from "../images/buttons/Edit.svg";
import ProfileAvatar from "./ProfileAvatar";

type ProfileData = {
    firstName?: string;
    lastName?: string;
    email?: string;
    telephone?: string;
    title?: string;
    company?: string;
    role?: string;
    avatarUrl?: string;      // normalized, absolute URL when set
    displayName?: string;
    sports?: Array<{ id?: number; name?: string }>;
    sportName?: string;
};

type Props = {
    loadUrl?: string;                          // endpoint to fetch profile; omit when data is provided
    data?: Partial<ProfileData>;               // when provided without loadUrl, renders without fetching
    onEdit?: () => void;                       // show pencil + custom edit handler when provided
    className?: string;
    mapResponse?: (json: any) => ProfileData;  // custom mapper if API differs
    userId: number | string;                   // kept for signature compatibility
    refreshTrigger?: number;                   // optional prop to trigger data refresh
    showEditButton?: boolean;                  // control whether to show edit button (defaults to true)
    /** When set, overrides automatic Admin badge (e.g. "LEAD" on student profile). */
    badgeLabelOverride?: string;
    badgeClassName?: string;
};

/* ---------- helpers ---------- */

/** Default mapping that tolerates multiple backend field names */
const defaultMap = (j: any): ProfileData => {
    const avatarCandidate =
        j?.profileImageUrl ??
        j?.imageUrl ??
        j?.avatarUrl ??
        j?.photoUrl ??
        j?.profile?.imageUrl ??
        j?.profile?.profileImageUrl ??
        null;

    return {
        firstName: j?.firstName,
        lastName: j?.lastName,
        email: j?.email,
        telephone: j?.telephone ?? j?.telephoneNumber,
        title: j?.title,
        company: j?.companyName ?? j?.company?.name,
        role: j?.role ?? j?.profileType ?? j?.roleName,
        avatarUrl: toAbsoluteUrl(avatarCandidate),
        displayName: j?.displayName,
        sports: Array.isArray(j?.sports) ? j.sports : undefined,
        sportName: j?.sportName,
    };
};

/* ---------- component ---------- */

export default function TeamMemberProfileHeader({
    className = "",
    mapResponse = defaultMap,
    loadUrl,
    data: profileData,
    onEdit,
    userId,
    refreshTrigger,
    showEditButton = true, // default to true
    badgeLabelOverride,
    badgeClassName,
}: Props) {
    const [data, setData] = useState<ProfileData | undefined>(() =>
        profileData ? mapResponse(profileData) : undefined
    );
    const [loading, setLoading] = useState<boolean>(!!loadUrl && !profileData);
    const [error, setError] = useState<Error | null>(null);
    const navigate = useNavigate();
    const hasDataRef = useRef(!!profileData);

    useEffect(() => {
        hasDataRef.current = !!data;
    }, [data]);

    // Check if current user can edit this profile
    const canEdit = canEditProfile(String(userId));

    const fetchProfile = React.useCallback(async () => {
        if (!loadUrl) return;

        const soft = hasDataRef.current;
        try {
            if (!soft) {
                setLoading(true);
            }
            setError(null);

            const res = await fetch(`${loadUrl}${loadUrl.includes('?') ? '&' : '?'}_t=${Date.now()}`, {
                method: "GET",
                headers: { "Content-Type": "application/json", Accept: "application/json" },
                credentials: "include"
            });
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            const json = await res.json();
            setData(mapResponse(json));
        } catch (e: any) {
            setError(e);
        } finally {
            setLoading(false);
        }
    }, [loadUrl, mapResponse]);

    useEffect(() => {
        if (!loadUrl) {
            if (profileData) {
                setData(mapResponse(profileData));
            }
            setLoading(false);
            setError(null);
            return;
        }

        void fetchProfile();
    }, [loadUrl, mapResponse, refreshTrigger, profileData, fetchProfile]);

    // Merge profile updates in place and soft-refetch for a fresh presigned avatar URL
    useEffect(() => {
        const handleProfileUpdate = (e: Event) => {
            const detail = (e as CustomEvent).detail as Partial<ProfileData> | undefined;
            if (detail) {
                setData((prev) => {
                    const mapped = mapResponse(detail);
                    return {
                        ...(prev ?? {}),
                        ...mapped,
                        // Keep coach sport chips when the event only carries sportIds.
                        sports: Array.isArray(mapped.sports) ? mapped.sports : prev?.sports,
                    };
                });
            }
            if (loadUrl) {
                void fetchProfile();
            }
        };

        window.addEventListener('profileUpdated', handleProfileUpdate);
        window.addEventListener('profilePictureUpdated', handleProfileUpdate);
        return () => {
            window.removeEventListener('profileUpdated', handleProfileUpdate);
            window.removeEventListener('profilePictureUpdated', handleProfileUpdate);
        };
    }, [loadUrl, mapResponse, fetchProfile]);

    const handleEdit = (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        localStorage.setItem("loggedInUserEdit", "false");
        if (onEdit) {
            onEdit();
            return;
        }
        navigate(`/add-admin-profile`);
    };

    if (loading) {
        return (
            <section className={`tm-profile-header ${className}`}>
                <div className="tm-profile-inner">
                    <div className="tm-profile-skeleton">
                        <div className="tm-profile-skeleton-avatar" />
                        <div className="tm-profile-skeleton-block">
                            <div className="tm-profile-skeleton-line tm-profile-skeleton-w60" />
                            <div className="tm-profile-skeleton-line tm-profile-skeleton-w30" />
                            <div className="tm-profile-skeleton-line tm-profile-skeleton-w80" />
                        </div>
                    </div>
                </div>
            </section>
        );
    }
    if (error) {
        return (
            <section className={`tm-profile-header ${className}`}>
                <div className="tm-profile-inner">
                    <div className="tm-profile-error">Error: {error.message}</div>
                </div>
            </section>
        );
    }
    if (!data) return null;

    const name =
        data.displayName ||
        [data.firstName, data.lastName].filter(Boolean).join(" ") ||
        "—";

    // Role badge mapping (override used for Team Lead on student profile)
    const roleRaw = (data.role || "").trim();
    const roleKey = roleRaw.toLowerCase();
    let badgeLabel = badgeLabelOverride ?? "";
    let badgeClass = badgeClassName ?? "";
    if (!badgeLabelOverride) {
        if (roleKey.includes("admin")) {
            badgeLabel = "Admin";
            badgeClass = "tm-profile-badge-admin";
        } else if (roleKey === "staff") {
            badgeLabel = "Staff";
            badgeClass = "tm-profile-badge-staff";
        } else if (roleKey === "coach") {
            badgeLabel = "Coach";
            badgeClass = "tm-profile-badge-coach";
        }
    } else if (!badgeClass) {
        badgeClass = "tm-profile-badge-admin";
    }

    const headerClassName = [
        "tm-profile-header",
        roleKey === "coach" ? "tm-profile-header--coach" : "",
        className,
    ].filter(Boolean).join(" ");

    return (
        <section className={headerClassName} aria-labelledby="profile-name">
            <div className="tm-profile-inner">
                {canEdit && showEditButton && (
                    <button
                        type="button"
                        className="tm-profile-edit"
                        aria-label="Edit profile"
                        onClick={handleEdit}
                        title="Edit profile"
                    >
                        <img src={EditIcon} alt="Edit" />
                    </button>
                )}

                <div className="tm-profile-header-row">
                    <div className="tm-profile-avatar-col">
                        <ProfileAvatar
                            displayName={name}
                            imageUrl={data.avatarUrl}
                            className="tm-profile-avatar"
                            alt=""
                            allowUpload={false}
                            skipAvatarFetch={true}
                        />
                    </div>

                    <div className="tm-profile-main-col">
                        <div className="tm-profile-title-block">
                            <h1 id="profile-name" className="tm-profile-name">{name}</h1>
                            {badgeLabel && <span className={`tm-profile-badge ${badgeClass}`}>{badgeLabel}</span>}
                            {(data.sports?.map((sport) => sport.name).filter(Boolean) ??
                                (data.sportName ? [data.sportName] : [])
                            ).map((sportName) => (
                                <span key={sportName} className="tm-profile-sport-chip">{sportName}</span>
                            ))}
                        </div>

                        {data.title && <div className="tm-profile-subtitle">{data.title}</div>}

                        <div className="tm-profile-grid tm-profile-contact-row">
                            <div className="tm-profile-field">
                                <span className="tm-profile-label">Email</span>
                                <span className="tm-profile-value">{data.email ?? "—"}</span>
                            </div>
                            <div className="tm-profile-field">
                                <span className="tm-profile-label">Phone Number</span>
                                <span className="tm-profile-value">{data.telephone ?? "—"}</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}
